import * as React from 'react'
import loadable from '@loadable/component'

import { useSiteConfig } from '@thg-commerce/enterprise-core'
import { Button } from '@thg-commerce/gravity-elements'
import { Grid } from '@thg-commerce/gravity-system'
import { spacing, theme } from '@thg-commerce/gravity-theme'

import {
  Facilities,
  StoreFacility,
} from '../StoreDetailsCard/Facilities/Facilities'
import { I18nText as StoreDetailsCardI18nText } from '../StoreDetailsCard/StoreDetailsCard'
import { formatTime, Status } from '../StoreDetailsCard/StoreStatus'
import MapMarkerIcon from '../StoreMap/MapMarkerIcon'
import { Address } from '../StoreMap/MapTypes'
import StoreMap from '../StoreMap/StoreMap'
import { StoreOpeningTime } from '../StoreSearch/types'

import { StoreDetailsHeading } from './StoreDetailsHeading'
import {
  AddressContainer,
  AddressText,
  ContactButton,
  ContactText,
  Container,
  EmailIconContainer,
  FacilitiesContainer,
  OpeningTimesContainer,
  OpeningTimesDayText,
  OpeningTimesStatusText,
  OpeningTimesText,
  SectionTitle,
  StoreInfoContainer,
  StyledMapWrapper,
} from './styles'

const TelephoneIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/TelephoneOutline'),
)

const EmailIcon = loadable(
  () => import('@thg-commerce/gravity-icons/src/components/EmailOutline'),
)

export interface Store {
  id: string
  displayName: string
  urlTag: string
  address: Address
  openingTimes: StoreOpeningTime[]
  status: Status
  relativeDistance: number
  storeFacility: StoreFacility[]
  phoneNumber?: string
  email?: string
  latitude: number
  longitude: number
}

export interface I18nText {
  storeInfoTitleText?: string
  storeOpeningTimesTitleText?: string
  storeFacilitiesTitleText?: string
  openText?: string
  closedText?: string
  getDirectionsButtonText?: string
  distanceAwayText?: string
  storeOpenText?: string
  storeClosedText?: string
  storeTempClosed?: string
  storePermClosed?: string
  email?: string
  storeDetailsCard: StoreDetailsCardI18nText
}

interface StoreDetailsProps {
  store: Store
  i18nText: I18nText
  apiKey?: string
  storeDetailsRoute: string
  getDirectionsHref: string
}

const EmailContent = ({ email }: { email: string }) => {
  return (
    <React.Fragment>
      <EmailIconContainer>
        <EmailIcon
          width={24}
          height={24}
          fill={theme.colors.palette.greys.darker}
        />
      </EmailIconContainer>
      <ContactButton emphasis="low" href={`mailto:${email}`}>
        {email}
      </ContactButton>
    </React.Fragment>
  )
}

const AddressContent = ({ address }: { address: Address }) => {
  return (
    <React.Fragment>
      <AddressText>
        {address.addressLine1} {address.addressLine2}
      </AddressText>
      {address.addressLine3 && (
        <AddressText>{address.addressLine3}</AddressText>
      )}
      {address.addressLine4 && (
        <AddressText>{address.addressLine4}</AddressText>
      )}
      {address.addressLine5 && (
        <AddressText>{address.addressLine5}</AddressText>
      )}
      <AddressText>{address.postalCode}</AddressText>
    </React.Fragment>
  )
}

export const StoreDetails = (props: StoreDetailsProps) => {
  const { hideStoreLocatorOpeningTimes } = useSiteConfig()

  const email = props.store.email || props.i18nText.email
  const currentDayIndex = new Date().getDay()
  const isPermanentlyClosed = props.store.status === Status.PERMANENTLY_CLOSED

  return (
    <div>
      <StoreDetailsHeading
        title={props.store.displayName}
        status={props.store.status}
        openingTimes={props.store.openingTimes[currentDayIndex]}
        isOpen={props.store.status === Status.OPEN}
        hideStoreLocatorOpeningTimes={hideStoreLocatorOpeningTimes}
        i18nText={{
          storeOpenText: props.i18nText?.storeOpenText,
          storeClosedText: props.i18nText?.storeClosedText,
          storeTempClosed: props.i18nText?.storeTempClosed,
          storePermClosed: props.i18nText?.storePermClosed,
        }}
      />
      <StyledMapWrapper>
        <StoreMap
          apiKey={props.apiKey}
          pinClickCallback={() => {}}
          markers={[
            {
              position: {
                lat: props.store.latitude,
                lng: props.store.longitude,
              },
              title: props.store.displayName,
              details: {
                title: props.store.displayName,
              },
              markerId: 1,
              customIcon: `data:image/svg+xml;charset=UTF-8,
                ${encodeURIComponent(MapMarkerIcon)}`,
              store: props.store,
            },
          ]}
          cardText={props.i18nText.storeDetailsCard}
          defaultZoom={15}
          selectedPinZoom={15}
          selectedMarkerId={1}
          showMarkerInfoWindow={false}
          mapControlOptions={{
            zoomControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
            ],
          }}
          center={{
            lat: props.store.latitude,
            lng: props.store.longitude,
          }}
          defaultPinColor={{
            fillColor: theme.colors.palette.accent.base,
            textColor: theme.colors.palette.greys.white,
          }}
          hoverPinColor={{
            fillColor: theme.colors.palette.accent.base,
            textColor: theme.colors.palette.greys.white,
          }}
          selectedPinColor={{
            fillColor: theme.colors.palette.accent.base,
            textColor: theme.colors.palette.greys.white,
          }}
        />
      </StyledMapWrapper>
      {isPermanentlyClosed ? (
        <Container isPermanentlyClosed={isPermanentlyClosed}>
          <AddressContainer isPermanentlyClosed={isPermanentlyClosed}>
            <AddressContent address={props.store.address} />
          </AddressContainer>
          {email && (
            <ContactText isPermanentlyClosed={isPermanentlyClosed}>
              <EmailContent email={email} />
            </ContactText>
          )}
        </Container>
      ) : (
        <Container>
          <StoreInfoContainer>
            <SectionTitle>{props.i18nText?.storeInfoTitleText}</SectionTitle>
            <AddressContainer>
              <AddressContent address={props.store.address} />
            </AddressContainer>
            <Button
              style={{ whiteSpace: 'nowrap' }}
              renderedAs="a"
              href={props.getDirectionsHref}
              target="_blank"
            >
              {props.i18nText?.getDirectionsButtonText}
            </Button>
            {props.store.phoneNumber && (
              <ContactText>
                <div
                  style={{
                    marginRight: spacing(1),
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <TelephoneIcon />
                </div>
                <ContactButton
                  emphasis="low"
                  href={`tel:${props.store.phoneNumber}`}
                >
                  {props.store.phoneNumber}
                </ContactButton>
              </ContactText>
            )}
            {email && (
              <ContactText>
                <EmailContent email={email} />
              </ContactText>
            )}
          </StoreInfoContainer>
          {!hideStoreLocatorOpeningTimes && (
            <OpeningTimesContainer>
              <SectionTitle>
                {props.i18nText?.storeOpeningTimesTitleText}
              </SectionTitle>
              <Grid columns={3} colGap={24}>
                {props.store.openingTimes.map((openingTimeObject, index) => {
                  const isTodaysOpeningTimeObject = currentDayIndex === index
                  if (openingTimeObject.closed) {
                    return (
                      <React.Fragment>
                        <OpeningTimesDayText
                          colStart={1}
                          isTodaysOpeningTimeObject={isTodaysOpeningTimeObject}
                        >
                          {openingTimeObject.day.toLowerCase()}
                        </OpeningTimesDayText>
                        <OpeningTimesStatusText
                          colStart={2}
                          isTodaysOpeningTimeObject={isTodaysOpeningTimeObject}
                        >
                          {props.i18nText?.closedText}
                        </OpeningTimesStatusText>
                      </React.Fragment>
                    )
                  }
                  return (
                    <React.Fragment>
                      <OpeningTimesDayText
                        colStart={1}
                        isTodaysOpeningTimeObject={isTodaysOpeningTimeObject}
                      >
                        {openingTimeObject.day.toLowerCase()}
                      </OpeningTimesDayText>
                      {props.store.status !== Status.TEMPORARILY_CLOSED && (
                        <OpeningTimesText
                          colStart={2}
                          isTodaysOpeningTimeObject={isTodaysOpeningTimeObject}
                        >
                          {`${formatTime(
                            openingTimeObject.openingTime,
                          )} - ${formatTime(openingTimeObject.closingTime)}`}
                        </OpeningTimesText>
                      )}
                      {isTodaysOpeningTimeObject ? (
                        props.store.status === Status.OPEN ? (
                          <OpeningTimesStatusText
                            colStart={3}
                            isTodaysOpeningTimeObject={
                              isTodaysOpeningTimeObject
                            }
                          >
                            {props.i18nText?.openText}
                          </OpeningTimesStatusText>
                        ) : (
                          <OpeningTimesStatusText
                            colStart={3}
                            isTodaysOpeningTimeObject={
                              isTodaysOpeningTimeObject
                            }
                          >
                            {props.i18nText?.closedText}
                          </OpeningTimesStatusText>
                        )
                      ) : (
                        props.store.status === Status.TEMPORARILY_CLOSED && (
                          <OpeningTimesStatusText
                            colStart={2}
                            isTodaysOpeningTimeObject={
                              isTodaysOpeningTimeObject
                            }
                          >
                            {props.i18nText?.closedText}
                          </OpeningTimesStatusText>
                        )
                      )}
                    </React.Fragment>
                  )
                })}
              </Grid>
            </OpeningTimesContainer>
          )}
          {props.store.storeFacility && props.store.storeFacility?.length > 0 && (
            <FacilitiesContainer>
              <SectionTitle>
                {props.i18nText?.storeFacilitiesTitleText}
              </SectionTitle>
              <Facilities storeFacility={props.store.storeFacility} />
            </FacilitiesContainer>
          )}
        </Container>
      )}
    </div>
  )
}
